import React, { useState, useRef } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Badge from '@amzn/awsui-components-console/badge';
import Avatar from '@amzn/awsui-chat-components-console/avatar';
import SupportPromptGroup from '@amzn/awsui-chat-components-console/support-prompt-group';
import Icon from '@amzn/awsui-components-console/icon';
import Popover from '@amzn/awsui-components-console/popover';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import ReactMarkdown from 'react-markdown';

import FeedbackDialog from '../pages/chat/additional-info/feedback-dialog';
import { FeedbackActions } from '../pages/chat/common-components';
import { FeedbackData, FeedbackResponse } from '../services/api';
import { useMetrics } from '../hooks/useMetrics';
import { METRICS_EVENTS, SUGGESTED_RESPONSE_CONTEXT } from '../constants';
import { SuggestedResponseBox } from './SuggestedResponseBox';
import { isPreProdEnvironment, hasFeatureAccess } from '../utils/environment';

interface Recommendation {
  type: string;
  summary: string;
  recommendation: string[];
  followup_questions_to_ask_customer: string[];
  next_best_actions_by_agent: string[];
  tools_used: string[];
  suggested_message?: string;
  timestamp?: string;
  isAdhoc?: boolean;
}

interface RecommendationBubbleProps {
  recommendation: Recommendation;
  onActionClick: (action: string) => void;
  index?: number;
  showLoadingBar?: boolean;
  onFeedbackSubmit?: (data: FeedbackData) => Promise<FeedbackResponse>;
  sessionId?: string | null;
  userPermissions?: string[];
}

export const RecommendationBubble: React.FC<RecommendationBubbleProps> = ({
  recommendation,
  onActionClick,
  index,
  showLoadingBar = false,
  onFeedbackSubmit,
  sessionId,
  userPermissions = []
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [isPositiveFeedback, setIsPositiveFeedback] = useState(false);
  const messageIdRef = useRef(`recommendation-${index ?? 0}-${Date.now()}`);
  const { logEvent } = useMetrics();
  const isPreProd = isPreProdEnvironment();
  const hasAccess = hasFeatureAccess(userPermissions);
  const showFeatures = isPreProd && hasAccess;

  const createRecommendationResponseObject = (recommendation: Recommendation, index?: number) => ({
    type: recommendation.type,
    summary: recommendation.summary,
    recommendations: recommendation.recommendation,
    actions: recommendation.next_best_actions_by_agent,
    timestamp: recommendation.timestamp,
    index: index
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'issue_detected': return 'red';
      case 'solution_identified': return 'green';
      case 'recommendations_provided': return 'blue';
      default: return 'grey';
    }
  };

  const formatTime = () => {
    if (recommendation.timestamp) {
      return new Date(recommendation.timestamp).toLocaleTimeString();
    }
    return new Date().toLocaleTimeString();
  };

  const getTypeIcon = () => {
    // Use the same Avatar component as existing chat bubbles
    return <Avatar color="gen-ai" iconName="gen-ai" ariaLabel="AI Assistant" />;
  };

  const renderFeedbackActions = () => {
    if (!onFeedbackSubmit || !sessionId) return null;

    return (
      <FeedbackActions
        contentToCopy={JSON.stringify(createRecommendationResponseObject(recommendation, index))}
        onFeedbackClick={(isPositive) => {
          setIsPositiveFeedback(isPositive);
          setShowFeedbackDialog(true);
        }}
        userMessage={`NBA_${recommendation.type}`}
        aiResponse={JSON.stringify(createRecommendationResponseObject(recommendation, index))}
        sessionId={sessionId}
        messageId={messageIdRef.current}
        hideCopy={true}
      />
    );
  };

  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      border: '1px solid #e1e4e8',
      borderRadius: '12px',
      padding: '16px',
      margin: '8px 0',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      position: 'relative',
      width: '100%'
    }}>
      {/* Chat bubble arrow pointing left */}
      <div style={{
        position: 'absolute',
        right: '-8px',
        top: '16px',
        width: '0',
        height: '0',
        borderTop: '8px solid transparent',
        borderBottom: '8px solid transparent',
        borderLeft: '8px solid #f8f9fa'
      }} />
      
      {/* Loading bar effect similar to tool calls */}
      {showLoadingBar && (
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          height: '3px',
          backgroundColor: '#e1e4e8',
          borderRadius: '12px 12px 0 0',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            backgroundColor: '#0073bb',
            animation: 'shimmer 2s infinite',
            backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
            backgroundSize: '200% 100%'
          }} />
        </div>
      )}
      
      <SpaceBetween size="s">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {getTypeIcon()}
            <span style={{ 
              fontSize: '12px', 
              fontWeight: 'bold',
              color: getTypeColor(recommendation.type) === 'red' ? '#d13212' : 
                     getTypeColor(recommendation.type) === 'green' ? '#037f0c' : 
                     getTypeColor(recommendation.type) === 'blue' ? '#0073bb' : '#5f6b7a',
              textTransform: 'uppercase'
            }}>
              {recommendation.type.replace(/_/g, ' ')}
            </span>
            {recommendation.isAdhoc && (
              <Badge color="blue">ON REQUEST</Badge>
            )}
            {!recommendation.isAdhoc && (
              <Badge color="green">REALTIME</Badge>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontSize: '12px', color: '#666', display: 'flex', gap: '8px' }}>
              {index !== undefined && <span>#{index + 1}</span>}
              <span>{formatTime()}</span>
            </div>
            {renderFeedbackActions()}
          </div>
        </div>
        
        <div style={{ 
          fontSize: '14px', 
          paddingBottom: '8px',
          borderBottom: '1px solid #e1e4e8',
          marginBottom: '12px'
        }}>
          <ReactMarkdown>{recommendation.summary}</ReactMarkdown>
        </div>

        {recommendation.suggested_message && (
          <SuggestedResponseBox 
            suggestedResponse={recommendation.suggested_message}
            onCopy={() => logEvent(METRICS_EVENTS.COPY_RECOMMENDATION_SUGGESTED_RESPONSE)}
            onFeedbackSubmit={onFeedbackSubmit}
            sessionId={sessionId}
            userMessage={`${SUGGESTED_RESPONSE_CONTEXT.RECOMMENDATION}_${recommendation.type.toLowerCase()}`}
          />
        )}

        {showFeatures && recommendation.next_best_actions_by_agent.length > 0 && (
              <SupportPromptGroup
            onItemClick={({ detail }) => {
              const actionIndex = parseInt(detail.id);
              const action = recommendation.next_best_actions_by_agent[actionIndex];
              onActionClick(action);
            }}
            alignment="horizontal"
            ariaLabel="Next best actions"
            items={recommendation.next_best_actions_by_agent.slice(0, 2).map((action, index) => ({
              text: action,
              id: index.toString()
            }))}
          />
        )}

        <ExpandableSection
          headerText="View Details"
          expanded={expanded}
          onChange={({ detail }) => {
            setExpanded(detail.expanded);
            if (detail.expanded) logEvent(METRICS_EVENTS.VIEW_RECOMMENDATION_DETAILS);
          }}
        >
          <SpaceBetween size="m">
            {recommendation.recommendation.length > 0 && (
              <Box>
                <Box fontWeight="bold" margin={{ bottom: 'xs' }}>Recommendations:</Box>
                <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                  {recommendation.recommendation.map((rec, index) => (
                    <li key={index} style={{ marginBottom: '4px' }}>{rec}</li>
                  ))}
                </ul>
              </Box>
            )}

            {recommendation.followup_questions_to_ask_customer.length > 0 && (
              <Box>
                <Box fontWeight="bold" margin={{ bottom: 'xs' }}>Follow-up Questions:</Box>
                <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                  {recommendation.followup_questions_to_ask_customer.map((question, index) => (
                    <li key={index} style={{ marginBottom: '4px' }}>{question}</li>
                  ))}
                </ul>
              </Box>
            )}

            {showFeatures && recommendation.next_best_actions_by_agent.length > 0 && (
              <Box>
                <Box fontWeight="bold" margin={{ bottom: 'xs' }}>All Next Best Actions:</Box>
                <SpaceBetween size="xs">
                  {recommendation.next_best_actions_by_agent.map((action, index) => (
                    <Button
                      key={index}
                      variant="normal"
                      onClick={() => onActionClick(action)}
                    >
                      {action}
                    </Button>
                  ))}
                </SpaceBetween>
              </Box>
            )}

            {recommendation.tools_used.length > 0 && (
              <Box>
                <Box fontWeight="bold" margin={{ bottom: 'xs' }}>Tools Used:</Box>
                <SpaceBetween size="xs" direction="horizontal">
                  {recommendation.tools_used.map((tool, index) => (
                    <Badge key={index} color="grey">{tool}</Badge>
                  ))}
                </SpaceBetween>
              </Box>
            )}
          </SpaceBetween>
        </ExpandableSection>
      </SpaceBetween>

      {showFeedbackDialog && onFeedbackSubmit && sessionId && (
        <FeedbackDialog
          onDismiss={() => setShowFeedbackDialog(false)}
          onSubmit={async (data) => {
            try {
              await onFeedbackSubmit(data);
              setShowFeedbackDialog(false);
            } catch (error) {
              console.error('Failed to submit feedback:', error);
              // Keep dialog open on error so user can retry
            }
          }}
          userMessage={`NBA_${recommendation.type}`}
          aiResponse={JSON.stringify(createRecommendationResponseObject(recommendation, index))}
          sessionId={sessionId}
          isPositive={isPositiveFeedback}
        />
      )}
    </div>
  );
};
